import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ResponseArtifactDTO,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
} from '@dreamai/types';
import api from '../../../api';
import ResponseArtifactModal from '../sidebar/ResponseArtifactModal';
import type { CardSelection } from '../sidebar/SpendingApprovalModal';
import ResearchBlockModal from './ResearchBlockModal';
import './artifact-opener.scss';

interface ArtifactOpenerProps {
  artifactId: string;
  /**
   * If true, render only the artifact modal (no opener button), opened by
   * default. Used when this component is mounted programmatically from
   * elsewhere (e.g. the generated-page iframe via postMessage). The caller
   * gets `onClose` to unmount when the user closes the modal.
   */
  hideButton?: boolean;
  defaultOpen?: boolean;
  onClose?: () => void;
}

const ArtifactOpener: React.FC<ArtifactOpenerProps> = ({
  artifactId,
  hideButton = false,
  defaultOpen = false,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(defaultOpen);

  useEffect(() => {
    let cancelled = false;
    const fetchArtifact = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifactId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && response.data.success) {
          setArtifact(response.data.artifact);
        }
      } catch (err) {
        console.error('ArtifactOpener: failed to fetch artifact', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchArtifact();
    return () => {
      cancelled = true;
    };
  }, [artifactId, getAccessTokenSilently]);

  const getLabel = useCallback((): string => {
    if (!artifact) return '';
    switch (artifact.artifactType) {
      case 'multiple_choice': {
        const count = artifact.choices?.length || 0;
        return `Choose from ${count} option${count !== 1 ? 's' : ''}`;
      }
      case 'yes_or_no':
        return 'Make a choice';
      case 'form_input':
        return 'Give details';
      default:
        return 'Read research';
    }
  }, [artifact]);

  const handleSubmitResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${art.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setArtifact((prev) =>
          prev ? { ...prev, status: 'actioned', userResponse: response } : prev,
        );
        setModalOpen(false);
      } catch (error) {
        console.error('ArtifactOpener: error submitting response', error);
      }
    },
    [getAccessTokenSilently],
  );

  if (loading || !artifact) return null;

  const isActioned = artifact.status === 'actioned';
  const isResearch = !['multiple_choice', 'yes_or_no', 'form_input'].includes(
    artifact.artifactType,
  );

  const handleClose = () => {
    setModalOpen(false);
    onClose?.();
  };

  return (
    <>
      {!hideButton && (
        <button
          className={`artifact-opener-tag${isActioned ? ' actioned' : ''}${isResearch ? ' research' : ''}`}
          onClick={() => setModalOpen(true)}
        >
          {isActioned ? '✓ ' : ''}
          {getLabel()}
        </button>
      )}
      {isResearch && artifact.projectAppId ? (
        <ResearchBlockModal
          show={modalOpen}
          onClose={handleClose}
          appId={artifact.projectAppId}
          title={artifact.title}
          artifactId={artifact.id}
          desiredActions={artifact.desiredActions}
          parentThreadId={artifact.parentThreadId}
        />
      ) : (
        <ResponseArtifactModal
          show={modalOpen}
          onClose={handleClose}
          artifacts={[artifact]}
          initialIndex={0}
          onSubmitResponse={handleSubmitResponse}
          readOnly={isActioned}
        />
      )}
    </>
  );
};

export default ArtifactOpener;
